import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { getAccounts } from '../services/accountService'
import { createCVU } from '../services/cvuService'
import { verifyPassword } from '../services/authService'
import { LuWallet, LuCirclePlus } from 'react-icons/lu'
import PasswordModal from '../components/PasswordModal'
import './CVUPage.css'

function CVUPage() {
  const { userRole } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [responseInfo, setResponseInfo] = useState(null)

  // Modal de confirmación
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [pendingCvuData, setPendingCvuData] = useState(null)
  const [verifying, setVerifying] = useState(false)

  // Form fields
  const [accountId, setAccountId] = useState('')
  const [cuit, setCuit] = useState('')
  const [name, setName] = useState('')

  useEffect(() => {
    loadAccounts()
  }, [])

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true)
      const response = await getAccounts()
      const accountsList = response.data || []
      setAccounts(accountsList)
      if (accountsList.length > 0) {
        setAccountId(accountsList[0].id)
      }
    } catch (err) {
      setError('Error al cargar cuentas')
    } finally {
      setLoadingAccounts(false)
    }
  }

  // Generar client_id automático (máximo 12 dígitos)
  const generateClientId = () => {
    // Usar timestamp + random para generar un ID único de máximo 12 dígitos
    const timestamp = Date.now().toString().slice(-9) // últimos 9 dígitos del timestamp
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0') // 3 dígitos random
    return parseInt(timestamp + random.slice(-3)) // Máximo 12 dígitos
  }

  const resetForm = () => {
    setCuit('')
    setName('')
    setError('')
    setSuccess('')
    setResponseInfo(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setResponseInfo(null)

    // Validaciones
    if (!accountId) {
      setError('Debe seleccionar una cuenta')
      return
    }

    if (!cuit) {
      setError('CUIT es requerido')
      return
    }

    if (!/^\d+$/.test(cuit)) {
      setError('CUIT debe contener solo números (sin guiones)')
      return
    }

    if (!name || name.length < 5 || name.length > 41) {
      setError('Nombre debe tener entre 5 y 41 caracteres')
      return
    }

    // Generar client_id automáticamente
    const clientId = generateClientId()

    const cvuData = {
      account_id: accountId,
      client_id: clientId,
      cuit,
      name,
      currency: 'ARS'  // Siempre ARS
    }

    // Guardar datos y mostrar modal de confirmación
    setPendingCvuData(cvuData)
    setShowPasswordModal(true)
  }

  const handlePasswordConfirm = async (password) => {
    setVerifying(true)
    
    try {
      // Re-autenticar usuario
      await verifyPassword(password)
      
      // Si el login es exitoso, cerrar modal y ejecutar operación
      setShowPasswordModal(false)
      
      // Ejecutar creación de CVU
      await executeCVUCreation(pendingCvuData)
      
      return { success: true }
    } catch (err) {
      console.error('Error de verificación:', err)
      return { 
        success: false, 
        error: err.response?.data?.error || 'Contraseña incorrecta'
      }
    } finally {
      setVerifying(false)
    }
  }

  const executeCVUCreation = async (cvuData) => {
    try {
      setLoading(true)
      const response = await createCVU(cvuData)
      
      setSuccess(`CVU dado de alta exitosamente!`)
      
      // Mostrar respuesta completa
      if (response.httpCode && response.fullResponse) {
        setResponseInfo({
          code: response.httpCode,
          message: response.message || `HTTP ${response.httpCode}`,
          fullResponse: response.fullResponse
        })
      }
      
      // Resetear formulario después de éxito
      setTimeout(() => {
        resetForm()
      }, 1000)
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al dar de alta CVU'
      setError(errorMsg)
    } finally {
      setLoading(false)
      setPendingCvuData(null)
    }
  }

  if (loadingAccounts) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando cuentas</p>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuWallet /> Dar de Alta CVU</h1>
      </div>

      {/* Modal de confirmación de contraseña */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false)
          setPendingCvuData(null)
        }}
        onConfirm={handlePasswordConfirm}
        title="Confirmar Alta de CVU"
        message="Por seguridad, ingrese su contraseña para confirmar la creación del CVU."
        loading={verifying}
      />

      {/* Mostrar mensaje de inhabilitado para CREDENCIAL */}
      {userRole === 'CREDENCIAL' && (
        <div className="disabled-feature">
          <div className="disabled-icon">⚠️</div>
          <h2>Funcionalidad Temporalmente Inhabilitada</h2>
          <p>Esta funcionalidad se encuentra inhabilitada temporalmente.</p>
          <p>Por favor, intente nuevamente más tarde.</p>
        </div>
      )}

      {/* Formulario solo para BIND */}
      {userRole !== 'CREDENCIAL' && (
        <>
          {responseInfo && (
            <div className="response-info">
              <div className="response-header">
                <strong>{responseInfo.message}</strong>
                <button 
                  className="btn-close-response" 
                  onClick={() => setResponseInfo(null)}
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
              <details open>
                <summary>Respuesta completa de Bind API</summary>
                <pre>{responseInfo.fullResponse}</pre>
              </details>
            </div>
          )}

          <form onSubmit={handleSubmit} className="cvu-form">
            <div className="form-section">
              <h3>Cuenta</h3>

              <div className="form-group">
                <label>Cuenta</label>
                <select 
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  className="form-control"
                  required
                >
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_routing?.address || acc.cbu || acc.cvu || acc.number} - {acc.type}{acc.label ? ` - ${acc.label}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3>Datos del Cliente</h3>

              <div className="form-group">
                <label>CUIT <span className="required">*</span></label>
                <input 
                  type="text"
                  value={cuit}
                  onChange={(e) => setCuit(e.target.value.replace(/[^0-9]/g, ''))}
                  className="form-control"
                  placeholder="20123456789"
                  required
                />
              </div>

              <div className="form-group">
                <label>Nombre <span className="required">*</span></label>
                <input 
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-control"
                  placeholder="Nombre del cliente"
                  minLength="5"
                  maxLength="41"
                  required
                />
              </div>

            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={resetForm}
                className="btn-secondary"
                disabled={loading}
              >
                Limpiar
              </button>
              <button 
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                <LuCirclePlus /> {loading ? 'Procesando...' : 'Dar de Alta CVU'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

export default CVUPage
